import { useEffect, useState } from 'react';
import { RotateCcw, Undo2 } from 'lucide-react';
import { SESSION_STATUS_LABEL, type SessionStatus } from '@doit/shared';
import { Modal } from '../../components/Modal';
import { Button, Field } from '../../components/ui';
import { Checkbox, Select, Textarea } from '../../components/form';
import { useToast } from '../../components/Toast';
import { useFormErrors } from '../../lib/useFormErrors';
import { useUpdateSession } from '../../api/queries';
import { ApiError } from '../../api/client';
import type { Session } from '../../api/types';

/** „HH:MM" → წუთები შუაღამიდან */
const toMin = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** წუთები → „HH:MM" (24 სთ-ის ფარგლებში) */
const toTime = (min: number) => {
  const v = ((min % 1440) + 1440) % 1440;
  return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
};

/** მენეჯერს შეუძლია ამ სამს შორის გადართვა; HELD ჟურნალიდან დგება */
const EDITABLE_STATUSES: SessionStatus[] = ['SCHEDULED', 'CANCELLED', 'POSTPONED'];

/**
 * კონკრეტული დღის განრიგის ცვლილება: დრო, თარიღი, გაუქმება, აღდგენის ნიშანი.
 * ინდივიდუალური დრო override-დება — ჯგუფის საერთო განრიგის შენახვა მას აღარ გადააწერს.
 */
export function SessionEditModal({
  session,
  onClose,
}: {
  session: Session | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const update = useUpdateSession(session?.group.id);
  const { fieldErrors, reset, handle } = useFormErrors();

  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [status, setStatus] = useState<string>('SCHEDULED');
  const [isMakeup, setIsMakeup] = useState(false);
  const [note, setNote] = useState('');

  const open = session !== null;
  const held = session?.status === 'HELD';
  const hasAttendance = (session?._count.attendance ?? 0) > 0;

  useEffect(() => {
    if (!session) return;
    reset();
    setDate(session.date.slice(0, 10));
    setStartTime(session.startTime);
    setEndTime(session.endTime ?? '');
    setStatus(session.status);
    setIsMakeup(session.isMakeup);
    setNote(session.note ?? '');
  }, [session, reset]);

  /** დაწყების შეცვლისას დასასრული იმავე მანძილზე მიჰყვება */
  function shiftStart(next: string) {
    if (/^\d{2}:\d{2}$/.test(next) && /^\d{2}:\d{2}$/.test(startTime) && endTime) {
      const span = toMin(endTime) - toMin(startTime);
      setEndTime(toTime(toMin(next) + span));
    }
    setStartTime(next);
  }

  async function submit() {
    if (!session) return;
    reset();
    try {
      await update.mutateAsync({
        id: session.id,
        data: { date, startTime, endTime, status, isMakeup, note },
      });
      toast.success('გაკვეთილი განახლდა');
      onClose();
    } catch (err) {
      handle(err);
    }
  }

  /** ინდივიდუალური დროის მოხსნა — ჯგუფის განრიგს უბრუნდება */
  async function followGroup() {
    if (!session) return;
    try {
      await update.mutateAsync({ id: session.id, data: { followGroup: true } });
      toast.success('გაკვეთილი ჯგუფის განრიგს დაუბრუნდა');
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ვერ შესრულდა');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={session ? `${session.group.name} · გაკვეთილი ${session.orderNo}` : ''}
      description="ამ დღის ცვლილება მხოლოდ ამ გაკვეთილს ეხება — ჯგუფის საერთო განრიგი უცვლელი რჩება"
      footer={
        <>
          {session?.timeOverride && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Undo2 className="size-4" />}
              onClick={() => void followGroup()}
              disabled={update.isPending}
              className="mr-auto"
            >
              ჯგუფის განრიგზე დაბრუნება
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={onClose} disabled={update.isPending}>
            გაუქმება
          </Button>
          <Button size="sm" onClick={() => void submit()} loading={update.isPending}>
            შენახვა
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="თარიღი"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            error={fieldErrors.date}
            disabled={hasAttendance}
            hint={hasAttendance ? 'დასწრება აღრიცხულია — თარიღი აღარ იცვლება' : undefined}
          />
          <Field
            label="დაწყება"
            type="time"
            value={startTime}
            onChange={(e) => shiftStart(e.target.value)}
            error={fieldErrors.startTime}
          />
          <Field
            label="დასრულება"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            error={fieldErrors.endTime}
          />
        </div>

        <Select
          label="სტატუსი"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          disabled={held}
          options={(held ? (['HELD'] as SessionStatus[]) : EDITABLE_STATUSES).map((s) => ({
            value: s,
            label: SESSION_STATUS_LABEL[s],
          }))}
          hint={
            held
              ? 'ჩატარებულია — სტატუსი ჟურნალიდან იმართება'
              : '„გაუქმებული" დღე პროცენტში არ ითვლება'
          }
        />

        <div className="rounded-lg border border-line bg-canvas/50 p-3">
          <Checkbox
            label="აღდგენითი გაკვეთილი"
            checked={isMakeup}
            onChange={setIsMakeup}
            hint="გაცდენილის ნაცვლად ტარდება — განრიგში ცალკე აღინიშნება"
          />
          {isMakeup && (
            <p className="mt-2 flex items-center gap-1.5 text-[12px] text-warn">
              <RotateCcw className="size-3.5" />
              აღდგენა
            </p>
          )}
        </div>

        <Textarea
          label="შენიშვნა"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          error={fieldErrors.note}
          placeholder="მაგ. ლექტორის თხოვნით გადავიდა 18:00-ზე"
        />
      </div>
    </Modal>
  );
}
